/**
 * ResultsSection - Run full-year optimization and display results
 *
 * Pre-flight checks:
 * 1. At least one zone configured
 * 2. Tariff selected (tariff_id present)
 * 3. Weather data loaded (ambient temps available)
 */

import React, { useEffect } from 'react';
import { Box, Button, CircularProgress, Alert, Typography, LinearProgress } from '@mui/material';
import { PlayArrow as OptimizeIcon, CheckCircle, Cancel, WarningAmber } from '@mui/icons-material';
import { SectionCard, vortxColors } from '@vortx/shared-ui';
import { useWeatherData } from '../../hooks/useWeatherData';
import ResultsChart from '../ResultsChart.jsx';

function PreflightCheck({ label, passed, detail }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {passed ? (
                <CheckCircle sx={{ color: '#2e7d32', fontSize: 18 }} />
            ) : (
                <Cancel sx={{ color: '#d32f2f', fontSize: 18 }} />
            )}
            <Typography variant="body2" sx={{ color: passed ? vortxColors.white : '#d32f2f' }}>
                {label}
            </Typography>
            {detail && (
                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                    ({detail})
                </Typography>
            )}
        </Box>
    );
}

export default function ResultsSection({ projectData, project, optimization, savedResults, onSaveResults, resultsStale }) {
    const { submitting, results, error, progress, submitAnnualOptimization, loadResults } = optimization;

    // Restore saved results on mount
    useEffect(() => {
        if (savedResults && !results) {
            loadResults(savedResults);
        }
    }, [savedResults, results, loadResults]);

    const lat = project?.location?.lat;
    const lon = project?.location?.lon;
    const { weatherData, loading: weatherLoading } = useWeatherData(lat, lon);

    // Pre-flight checks
    const hasZones = projectData?.zones?.length > 0;
    const tariffId = project?.meters?.main?.tariff?.id;
    const tariffName = project?.meters?.main?.tariff?.name;
    const hasTariff = !!tariffId;
    const hasWeather = !!weatherData?.hourlyTemps && weatherData.hourlyTemps.length === 8760;
    const canOptimize = hasZones && hasTariff && !weatherLoading && hasWeather;

    const handleOptimize = async () => {
        try {
            const annualResults = await submitAnnualOptimization(
                projectData,
                weatherData.hourlyTemps,
                { tariff_id: tariffId }
            );

            // Persist summary to Firestore
            if (annualResults && onSaveResults) {
                onSaveResults(annualResults, 'annual');
            }
        } catch (err) {
            // Error already set in hook
        }
    };

    return (
        <Box>
            <SectionCard
                title="Pre-flight Checks"
                description="All requirements must be met before running optimization"
            >
                <Box sx={{ mb: 2 }}>
                    <PreflightCheck
                        label="Zones configured"
                        passed={hasZones}
                        detail={hasZones ? `${projectData.zones.length} zone(s)` : 'Add at least one zone in Facility tab'}
                    />
                    <PreflightCheck
                        label="Tariff selected"
                        passed={hasTariff}
                        detail={hasTariff ? tariffName : 'Configure tariff in Meters & Tariffs tab'}
                    />
                    <PreflightCheck
                        label="Weather data loaded"
                        passed={hasWeather}
                        detail={
                            weatherLoading ? 'Loading...' :
                            hasWeather ? `8760 hourly temps, peak ${Math.max(...weatherData.hourlyTemps).toFixed(0)}°F` :
                            (lat == null || lon == null || (lat === 0 && lon === 0)) ? 'Set project location on Projects page' :
                            'Failed to load'
                        }
                    />
                </Box>
            </SectionCard>

            <SectionCard
                title="Run Optimization"
                description="Optimize compressor schedules across all 12 months to minimize annual energy costs"
                sx={{ mt: 3 }}
            >
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Progress bar during annual solve */}
                {submitting && progress && (
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                Optimizing {progress.month}...
                            </Typography>
                            <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                {progress.current} / {progress.total}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={(progress.current / progress.total) * 100}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'rgba(33, 150, 243, 0.15)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    background: 'linear-gradient(90deg, #2196F3, #42a5f5)',
                                },
                            }}
                        />
                    </Box>
                )}

                <Button
                    variant="contained"
                    size="large"
                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <OptimizeIcon />}
                    onClick={handleOptimize}
                    disabled={submitting || !canOptimize}
                    sx={{
                        background: `linear-gradient(135deg, #2196F3 0%, #42a5f5 100%)`,
                        color: '#fff',
                        '&:hover': {
                            background: `linear-gradient(135deg, #1976d2 0%, #2196F3 100%)`,
                        },
                        '&:disabled': {
                            background: vortxColors.stone,
                        },
                    }}
                >
                    {submitting ? `Optimizing ${progress?.month || ''}...` : 'Run Full Year Optimization'}
                </Button>
            </SectionCard>

            {(results || submitting) && (
                <SectionCard title="Results" sx={{ mt: 3 }}>
                    {/* Stale results warning */}
                    {resultsStale && !submitting && (
                        <Alert
                            severity="warning"
                            icon={<WarningAmber />}
                            sx={{
                                mb: 2,
                                bgcolor: 'rgba(237, 108, 2, 0.1)',
                                border: '1px solid rgba(237, 108, 2, 0.3)',
                                '& .MuiAlert-icon': { color: '#ed6c02' },
                            }}
                        >
                            <Typography variant="body2" sx={{ color: vortxColors.white }}>
                                <strong>Inputs have changed</strong> since the last optimization run.
                                These results may no longer be accurate. Re-run optimization to update.
                            </Typography>
                        </Alert>
                    )}
                    <Box sx={resultsStale && !submitting ? { opacity: 0.45, filter: 'grayscale(40%)' } : {}}>
                        {submitting && !results ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress sx={{ color: '#2196F3' }} />
                            </Box>
                        ) : (
                            <ResultsChart
                                results={results}
                                zones={projectData?.zones}
                            />
                        )}
                    </Box>
                </SectionCard>
            )}
        </Box>
    );
}
